import { Component, OnInit, OnDestroy } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import moment from 'moment-timezone';
import { NgxSpinnerService } from 'ngx-spinner';
import { LanguageTranslateService } from "src/app/_services/language-translate.service";
import { ProjectService } from 'src/app/_services/project.service';
import { UtilServiceService } from 'src/app/_services/utilService/util-service.service';
import { ErrorUtil } from 'src/app/_utilities/error';
import { SubscriptionUtil } from 'src/app/_utilities/subscription';

@Component({
  selector: 'app-create-milestones',
  standalone: false,
  templateUrl: './create-milestones.component.html',
  styleUrl: './create-milestones.component.scss'
})
/**
 * Create / edit milestone popup.
 * mode 'add'  -> POST /project-milestones. Status is locked to Open and is not sent.
 * mode 'edit' -> GET /project-milestones/{id} to fill the form, PUT /project-milestones to save.
 * Edit rules: start date is disabled, status is editable (OPEN | IN_PROGRESS | COMPLETED).
 * End date cannot be before start date. Spinner shows while save is in progress.
 * ngx-bootstrap copies modal initialState onto this component (`mode`, `data`) before ngOnInit.
 */
export class CreateMilestonesComponent
  extends SubscriptionUtil
  implements OnInit, OnDestroy {
  translate: any;
  /** Add: { projectId }. Edit: milestone row, must include id. */
  data: any;
  /** 'add' from createMilestoneProject, 'edit' from editMilestoneProject. */
  mode: any;
  dateConfig: any;
  milestoneForm!: FormGroup;
  private milestoneId: any;
  private projectId: any;
  saveInProgress = false;
  submitted = false;

  statusOptions = [
    { label: 'Open', value: 'Open' },
    { label: 'In Progress', value: 'In Progress' },
    { label: 'Completed', value: 'Completed' },
  ];

  constructor(
    public modalRef: BsModalRef,
    private languageTranslateService: LanguageTranslateService,
    private modalServc: BsModalService,
    private projectService: ProjectService,
    private errorUtil: ErrorUtil,
    private spinner: NgxSpinnerService,
    private util: UtilServiceService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.dateConfig = {
      dateInputFormat: localStorage.getItem("date_format")!.toUpperCase(),
    };
    this.getLanguageData();
    this.milestoneForm = new FormGroup({
      name: new FormControl(null, [Validators.required]),
      description: new FormControl(null),
      // Start date stays locked in edit; getRawValue() still includes it on PUT.
      startDate: new FormControl({ value: null, disabled: this.mode === 'edit' }, [Validators.required]),
      endDate: new FormControl(null, [Validators.required]),
      // Status is fixed to Open while creating, and editable while editing.
      status: new FormControl({ value: 'Open', disabled: this.mode !== 'edit' }),
    });

    if (this.mode === 'edit') {
      this.loadMilestone();
    }
  }

  /** GET /project-milestones/{id}. Fills the form with the saved milestone. */
  private loadMilestone() {
    const id = this.data?.id;
    if (!id) {
      return;
    }
    this.push(
      this.projectService.getProjectMilestoneById(id).subscribe({
        next: (res: any) => {
          const milestone = res?.body?.data ?? res?.body ?? res;
          this.milestoneId = milestone?.id ?? id;
          this.projectId = milestone?.project?.id ?? this.data?.project?.id ?? this.data?.projectId;
          this.milestoneForm.patchValue({
            name: milestone?.name ?? null,
            description: milestone?.description ?? null,
            startDate: this.fromApiDate(milestone?.startDate),
            endDate: this.fromApiDate(milestone?.endDate),
            status: this.toFormStatus(milestone?.status),
          });
        },
        error: (err: any) => {
          this.errorUtil.setErrorMessage(
            err.status,
            null,
            err?.error?.title || err?.error?.message,
            'error',
            2000,
          );
        },
      })
    );
  }

  getLanguageData() {
    this.push(
      this.languageTranslateService.wordSrc$.subscribe((data) => {
        if (Object.keys(data).length != 0) {
          this.translate = data;
          this.statusOptions = [
            { label: data?.general?.status?.open?.singular || 'Open', value: 'Open' },
            { label: data?.general?.status?.inProgress?.singular || 'In Progress', value: 'In Progress' },
            { label: data?.general?.status?.completed?.singular || 'Completed', value: 'Completed' },
          ];
        }
      }),
    );
  }

  push(obs: any) {
    super.push(obs);
  }

  onDateChange() {
    this.validateDateRange();
  }

  onSave() {
    this.submitted = true;
    this.validateDateRange();
    if (this.milestoneForm.invalid || this.saveInProgress) {
      this.milestoneForm.markAllAsTouched();
      return;
    }

    const formValue = this.milestoneForm.getRawValue();
    const projectSource = this.mode === 'edit' ? this.projectId : this.data?.projectId;
    const projectId = Number(projectSource);
    const payload: any = {
      name: String(formValue.name).trim(),
      description: formValue.description ? String(formValue.description).trim() : '',
      startDate: this.toApiDate(formValue.startDate),
      endDate: this.toApiDate(formValue.endDate),
      project: {
        id: Number.isNaN(projectId) ? projectSource : projectId,
      },
    };

    // PUT body: id, name, description, startDate, endDate, status, project.id
    if (this.mode === 'edit') {
      payload.id = this.milestoneId ?? this.data?.id;
      payload.status = this.toApiStatus(formValue.status);
    }

    const request = this.mode === 'edit'
      ? this.projectService.updateProjectMilestone(payload)
      : this.projectService.createProjectMilestone(payload);

    this.saveInProgress = true;
    this.spinner.show();
    this.push(
      request.subscribe({
        next: (res: any) => {
          this.saveInProgress = false;
          this.spinner.hide();
          if (res.status === 200 || res.status === 201) {
            this.errorUtil.setErrorMessage(
              200,
              this.translate?.general.messages.savedSuccessfully,
              null,
              'success',
              2000,
            );
            this.modalServc.setDismissReason(JSON.stringify({ reason: 'data_added' }));
            this.modalRef.hide();
          }
        },
        error: (err: any) => {
          this.saveInProgress = false;
          this.spinner.hide();
          this.errorUtil.setErrorMessage(
            err.status,
            null,
            err?.error?.title || err?.error?.message,
            'error',
            2000,
          );
        },
      })
    );
  }

  /** Ensure start date is not after end date. Sets afterEnd / beforeStart errors. */
  private validateDateRange() {
    const startCtrl = this.milestoneForm.get('startDate');
    const endCtrl = this.milestoneForm.get('endDate');
    this.clearError(startCtrl, 'afterEnd');
    this.clearError(endCtrl, 'beforeStart');

    const start = startCtrl?.value;
    const end = endCtrl?.value;
    if (!start || !end) {
      return;
    }

    const startDay = new Date(start);
    const endDay = new Date(end);
    startDay.setHours(0, 0, 0, 0);
    endDay.setHours(0, 0, 0, 0);

    if (startDay.getTime() > endDay.getTime()) {
      startCtrl?.setErrors({ ...(startCtrl.errors || {}), afterEnd: true });
      endCtrl?.setErrors({ ...(endCtrl.errors || {}), beforeStart: true });
    }
  }
  /** Clear a specific validation error from a form control. */
  private clearError(control: AbstractControl | null, key: string) {
    if (!control?.errors?.[key]) {
      return;
    }
    const errors = { ...control.errors };
    delete errors[key];
    control.setErrors(Object.keys(errors).length ? errors : null);
  }

  /** API status (OPEN, IN_PROGRESS, COMPLETED) -> dropdown label value. */
  private toFormStatus(status: any): string {
    const value = typeof status === 'string' ? status : status?.name || status?.value || '';
    const normalized = String(value).replace(/_/g, ' ').toLowerCase();
    if (normalized === 'in progress') {
      return 'In Progress';
    }
    if (normalized === 'completed') {
      return 'Completed';
    }
    return 'Open';
  }

  /** Dropdown value -> API status enum sent on PUT. */
  private toApiStatus(status: any): string {
    const normalized = String(status || '').replace(/_/g, ' ').toLowerCase();
    if (normalized === 'in progress') {
      return 'IN_PROGRESS';
    }
    if (normalized === 'completed') {
      return 'COMPLETED';
    }
    return 'OPEN';
  }

  /**
   * Calendar day at midnight in the user timezone.
   * timeFormatter applies the user timezone, then toISOString sends that instant.
   */
  private toApiDate(value: any): string | null {
    const zoned = this.zonedMidnight(value);
    return zoned ? zoned.toISOString() : null;
  }

  /** Same timezone midnight, as a Date the calendar can show. */
  private fromApiDate(value: any): Date | null {
    const zoned = this.zonedMidnight(value);
    return zoned ? zoned.toDate() : null;
  }

  private zonedMidnight(value: any) {
    if (!value) {
      return null;
    }
    const day = moment(value, 'YYYY-MM-DD').format('YYYY-MM-DD');
    if (day === 'Invalid date') {
      return null;
    }
    return this.util.timeFormatter(
      moment('00:00:00', 'h:mm A').format('h:mm A'),
      day,
    );
  }
}
